"use client";

/**
 * Catches what the error screens can't: errors outside rendering (a failed
 * promise nobody awaited, an exception in an event handler or timer). They
 * are reported to the API log like screen errors (lib/errorReport.ts), at most
 * a few per page load and each message once, and noise is ignored (browser
 * extensions, cross-origin "Script error.", cancelled requests, ResizeObserver).
 *
 * The "new version is ready" notice appears only when it is real: an app file
 * of THIS site (/_next/static/… script or stylesheet, not a prefetch hint, not
 * another site's or an extension's file) fails to load MID-SESSION — after the
 * page finished loading — a fresh re-request of that same file fails too, AND
 * the server says a different build is live (/build-id ≠ this page's build).
 * Same build live means a reload can't help: nothing is shown, and the file is
 * reported to the API log ("Static file") so it can be fixed. Failures while the page is still loading are
 * ignored here (a dev server still rebuilding, a flaky connection); if they
 * do break the page, the error screen reloads it instead. Once dismissed or
 * acted on, it stays hidden for this browser session. Mounted once in the
 * root layout.
 */
import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { reportError } from "@/lib/errorReport";

const MAX_REPORTS = 8;
const MINE = process.env.NEXT_PUBLIC_BUILD_ID ?? "";
const GRACE_MS = 5000;
const HIDE_KEY = "rb_update_notice_hidden";

function sessionHidden(): boolean {
  try {
    return window.sessionStorage.getItem(HIDE_KEY) === "1";
  } catch {
    return false;
  }
}

function hideForSession() {
  try {
    window.sessionStorage.setItem(HIDE_KEY, "1");
  } catch {
    /* storage blocked */
  }
}
const IGNORE = /ResizeObserver loop|Script error\.?$|AbortError|The user aborted|Load failed$|cancell?ed|NetworkError when attempting to fetch|Non-Error promise rejection captured with value: undefined/i;

function asError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

export function GlobalErrorListener() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const seen = new Set<string>();
    let sent = 0;
    const send = (err: Error, boundary: string, source?: string) => {
      const msg = `${err.name}: ${err.message}`;
      if (!err.message || IGNORE.test(msg) || IGNORE.test(err.name)) return;
      if (source && /^(chrome|moz|safari)-extension:|^webkit-masked-url:/.test(source)) return;
      if (seen.has(msg) || sent >= MAX_REPORTS) return;
      seen.add(msg);
      sent += 1;
      reportError(err, boundary, false);
    };

    // Only failures after the page has fully loaded (plus a grace period) count.
    let settled = false;
    const settle = () => window.setTimeout(() => { settled = true; }, GRACE_MS);
    if (document.readyState === "complete") settle();
    else window.addEventListener("load", settle, { once: true });

    const checking = new Set<string>();
    const verify = (url: string) => {
      if (checking.has(url)) return;
      checking.add(url);
      // Re-request the same file, bypassing caches: a file that loads now was a blip.
      fetch(url, { cache: "no-store", method: "GET" })
        .then(async (r) => {
          if (r.ok || r.status < 400 || r.status >= 500) return;
          // Missing for real. Only a NEWER build being live makes "reload" the answer.
          const live = await fetch(`/build-id?t=${Date.now()}`, { cache: "no-store" })
            .then((b) => (b.ok ? (b.json() as Promise<{ id?: string }>) : null))
            .catch(() => null);
          // One message about it: the build watcher's note may already be on screen.
          if (live?.id && MINE && live.id !== MINE) setStale(!document.querySelector("[data-rb-build-note]"));
          else if (live?.id) send(new Error(`App file missing in the live build (${r.status}): ${url.split("?")[0]}`), "Static file");
        })
        .catch(() => undefined) // offline: not a new version
        .finally(() => checking.delete(url));
    };
    const ours = (url: string) => {
      try {
        const u = new URL(url, window.location.href);
        return u.origin === window.location.origin && u.pathname.startsWith("/_next/static/");
      } catch {
        return false;
      }
    };

    const onError = (e: ErrorEvent | Event) => {
      const target = e.target as HTMLElement | null;
      // A file that failed to load (script, stylesheet).
      if (target && target !== (window as unknown as HTMLElement) && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
        if (target.tagName === "LINK") {
          const rel = ((target as HTMLLinkElement).rel || "").toLowerCase();
          if (rel !== "stylesheet") return; // prefetch / preload hints never break a page
        }
        const url = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || "";
        if (settled && ours(url) && !sessionHidden()) verify(url);
        return;
      }
      const ev = e as ErrorEvent;
      if (ev.error || ev.message) send(asError(ev.error ?? ev.message), "Window", ev.filename);
    };
    const onRejection = (e: PromiseRejectionEvent) => send(asError(e.reason), "Promise");

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  if (!stale || dismissed) return null;
  return (
    <div role="status" className="fixed left-1/2 top-4 z-[120] w-[calc(100%-2rem)] max-w-[460px] -translate-x-1/2 rounded-2xl bg-[#140f33] p-3.5 text-white shadow-2xl ring-1 ring-white/10" data-no-track>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#614ae7]"><RefreshCw size={16} /></span>
        <div className="min-w-0 flex-1 text-[13px]">
          <div className="font-bold">A new version of Rankbist is ready</div>
          <div className="text-white/65">Reload to keep everything working.</div>
        </div>
        <button type="button" onClick={() => { hideForSession(); window.location.reload(); }} className="rounded-xl bg-white px-3.5 py-2 text-[12.5px] font-bold text-[#614ae7] hover:bg-white/90">
          Reload
        </button>
        <button type="button" onClick={() => { hideForSession(); setDismissed(true); }} aria-label="Dismiss" className="rounded-lg p-1 text-white/50 hover:bg-white/10 hover:text-white">
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
